import classNames from "classnames/bind";
import React from "react";
import type { ItemBaseType } from "../../DataTable/types";
import { Pagination } from "../../Pagination";
import { CtaCard } from "../CtaCard";
import { PrimaryCard } from "../PrimaryCard";
import { SchemaCard } from "../SchemaCard";
import styles from "./CardGrid.module.scss";
import type { CardGridProps } from "./types";

const cx = classNames.bind(styles);

const skeletonDefaultData = {
  title: "",
  description: "",
  author: "",
  pills: [],
  created_at: null,
  updated_at: null,
};

// TODO: this component + stories needs a full rework.
/**
 * a grid to display our card components
 */
const CardGrid = <ItemType extends ItemBaseType>({
  items = [],
  ctaTitle = "Create Dashboard",
  hasCta = true,
  listCardType = "primary",
  onCtaClick,
  onPrimaryCardClick,
  isLoading,
  onSelectionChange,
  getMeta,
  pagination = {
    page: 1,
    pageSize: 10,
    total: 0,
    pageSizeText: "cards per page",
  },
  onPaginationChange: onPaginationChangeCallback,
  cardConfig = {
    hasGreyBar: true,
    hasDescriptionLabel: true,
    hasReducedHeight: false,
  },
}: CardGridProps<ItemType>) => {
  // provides a range of 8 items to draw skeletons while loading
  const validItems = isLoading
    ? Array.from(Array(8).keys()).map(() => skeletonDefaultData)
    : items;

  // the first card is the CTA Card by default and may be disabled if it not required like on the Form Templates page
  const cards = hasCta
    ? [
        <div
          className={cx("grid-child")}
          key={"ctaCard"}
        >
          <CtaCard
            title={ctaTitle}
            isLoading={isLoading}
            onClick={onCtaClick}
          />
        </div>,
      ]
    : [];

  const getCard = (card: any) => {
    const date =
      card.updated_at && card.updated_at !== ""
        ? { updatedAt: new Date(card.updated_at) }
        : { createdAt: new Date(card.created_at) };

    switch (listCardType) {
      case "schema":
        return (
          <SchemaCard
            id={card.id}
            title={card.name}
            description={card.description}
            category={card.form_category?.name ?? null}
            date={date.updatedAt ?? date.createdAt}
            onClick={onPrimaryCardClick}
            isLoading={isLoading}
            cardConfig={cardConfig}
          />
        );
      case "primary":
      default:
        return (
          <PrimaryCard
            id={card.id}
            title={card.name}
            description={card.description}
            pills={card.pills}
            onClick={onPrimaryCardClick}
            isLoading={isLoading}
            meta={getMeta?.(date)}
            cardConfig={cardConfig}
          />
        );
    }
  };

  // after the CTA, the remaining cards are all Primary Cards
  validItems.forEach((card: any, index) => {
    // occasionally some cards have the same id which is a backend issue the FE can't control
    // ideally we should use id but I have seen cases on staging where the ids are not unique
    cards.push(
      <div
        className={cx("grid-child")}
        key={`${card.id}${index}`}
      >
        {getCard(card)}
      </div>
    );
  });

  const onPaginationChange: CardGridProps<ItemType>["onPaginationChange"] = (
    newPagination
  ) => {
    if (onSelectionChange) {
      onSelectionChange([]);
    }

    if (onPaginationChangeCallback) {
      onPaginationChangeCallback(newPagination);
    }
  };

  const showPagination =
    pagination.page !== undefined && pagination.pageSize !== undefined;

  return (
    <>
      <div className={cx("grid-parent")}>{cards}</div>
      {showPagination ? (
        <Pagination
          {...pagination}
          pageTotal={items.length}
          className={cx("footer")}
          onChange={onPaginationChange}
        />
      ) : null}
    </>
  );
};

export default CardGrid;
